/** Insignia de estado en pildora con punto de color (design.md §22.7). */
import { clsx } from 'clsx';

type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

type StatusConfig = {
  label: string;
  variant: StatusVariant;
};

const statusMap: Record<string, StatusConfig> = {
  open: { label: 'Pendiente', variant: 'warning' },
  unavailable: { label: 'No disponible', variant: 'neutral' },
  approved: { label: 'Aprobado', variant: 'success' },
  rejected: { label: 'Rechazado', variant: 'error' },
  cancelled: { label: 'Cancelado', variant: 'neutral' },
  reassigned: { label: 'Reasignado', variant: 'info' },
  draft: { label: 'Borrador', variant: 'neutral' },
  internal_review: { label: 'Revisión interna', variant: 'info' },
  internal_approved: { label: 'Aprobado internamente', variant: 'success' },
  sent: { label: 'Enviado', variant: 'info' },
  client_approved: { label: 'Aprobado por cliente', variant: 'success' },
  client_rejected: { label: 'Rechazado por cliente', variant: 'error' },
  expired: { label: 'Vencido', variant: 'error' },
  technical_review: { label: 'Revisión técnica', variant: 'info' },
  observed: { label: 'Observado', variant: 'warning' },
  in_design: { label: 'En diseño', variant: 'info' },
  design_approved: { label: 'Diseño aprobado', variant: 'success' },
  on_budget: { label: 'En presupuesto', variant: 'info' },
  completed: { label: 'Completado', variant: 'success' },
  commercial: { label: 'Comercial', variant: 'info' },
  superseded: { label: 'Reemplazado', variant: 'neutral' },
  active: { label: 'Activo', variant: 'success' },
  inactive: { label: 'Inactivo', variant: 'neutral' },
  pending: { label: 'Pendiente', variant: 'warning' },
  processing: { label: 'Procesando', variant: 'info' },
};

const variantStyles: Record<StatusVariant, string> = {
  success: 'bg-success-light text-success-dark',
  warning: 'bg-warning-light text-warning-dark',
  error: 'bg-error-light text-error-dark',
  info: 'bg-info-light text-info-dark',
  neutral: 'bg-bg-tertiary text-text-secondary',
};

const dotStyles: Record<StatusVariant, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  info: 'bg-info',
  neutral: 'bg-text-tertiary',
};

type StatusBadgeProps = {
  status: string;
  size?: 'sm' | 'md';
  showIcon?: boolean;
};

export function StatusBadge({ status, size = 'md', showIcon = true }: StatusBadgeProps) {
  const config: StatusConfig = statusMap[status] || {
    label: status.replace(/_/g, ' '),
    variant: 'neutral',
  };

  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center gap-1.5 rounded-full font-medium',
        variantStyles[config.variant],
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
      )}
    >
      {showIcon && (
        <span
          aria-hidden="true"
          className={clsx('shrink-0 rounded-full', dotStyles[config.variant], size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2')}
        />
      )}
      <span className="truncate">{config.label}</span>
    </span>
  );
}

/** Insignia generica para contadores o etiquetas. */
type BadgeProps = {
  children: React.ReactNode;
  variant?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md';
};

const badgeVariantStyles = {
  primary: 'bg-primary text-white',
  success: 'bg-success-light text-success-dark',
  warning: 'bg-warning-light text-warning-dark',
  error: 'bg-error-light text-error-dark',
  info: 'bg-info-light text-info-dark',
  neutral: 'bg-bg-tertiary text-text-secondary',
};

export function Badge({ children, variant = 'primary', size = 'md' }: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded-full font-semibold',
        badgeVariantStyles[variant],
        size === 'sm' ? 'min-w-[20px] px-1.5 py-0.5 text-xs' : 'min-w-[24px] px-2 py-0.5 text-sm',
      )}
    >
      {children}
    </span>
  );
}
